/**
 * Kameleoon Service
 *
 * Self-Registration: Dieser Service registriert sich automatisch beim Import.
 * Consent-Modus: MIT Usercentrics-Consent (requiresConsent: true)
 *
 * Verwaltet A/B-Testing und Conversion-Tracking über Kameleoon REST API.
 * Tracked verschiedene User-Journeys und Conversion-Events auf der Website.
 *
 * Nutzt die Kameleoon Data API direkt statt der JavaScript SDK API für
 * zuverlässigeres und unabhängigeres Tracking.
 *
 * Um den Service zu entfernen: Einfach den gesamten kameleoon Ordner löschen
 * und den Import in services/index.js entfernen.
 */
import { registerEventHandler } from "../../../event-registry";
import serviceRegistry from "../ServiceRegistry";
import kameleoonInteg from "./data-integ";
import kameleoonProd from "./data-prod";

export { initKameleoon, KAMELEOON_CONFIG };

// Kameleoon Goal IDs
const GOALS = {
	PDP: 395528,
	ADD_TO_CART: 395529,
	CART_PAGE: 395530,
	ORDER_OVERVIEW: 395531,
	ORDER_WITH_REVENUE: 395532,
	AVG_ORDER_QUANTITY: 395533,
	REGISTRATION: 395535,
	SEARCH: 395536
};

// Kameleoon Custom Data IDs
const CUSTOM_DATA = {
	BESTELLNUMMER: 1,
	BESTELLTYP: 4,
	ZAHLART: 5
};

// Kameleoon API Konfiguration
const KAMELEOON_CONFIG = {
	// Data API Endpoint (EU Region)
	API_ENDPOINT: 'https://eu-data.kameleoon.io',
	// Cookie-Name für Visitor Code
	VISITOR_COOKIE: 'kameleoonVisitorCode',
	// API-Modus: 'rest' für REST API, 'client' für Client API (Command Queue)
	API_MODE: 'client'
};

// Site Code wird aus der umgebungsspezifischen Konfiguration geladen
let SITE_CODE = null;

/**
 * Initialisiert den Kameleoon Service
 * @param {Object} dps - DataProcessingService Instanz mit datalayerModel und serviceSettings
 */
async function initKameleoon(dps) {
	try {
		// Site Code aus der umgebungsspezifischen Konfiguration laden
		SITE_CODE = dps.serviceSettings?.SITE_CODE;
		if (!SITE_CODE) {
			console.error('Kameleoon: SITE_CODE nicht in der Konfiguration gefunden. Service wird nicht initialisiert.');
			return;
		}

		window.kameleoonQueue = window.kameleoonQueue || [];

		// Legal Consent aktivieren via Queue (wird ausgeführt sobald engine.js bereit ist)
		window.kameleoonQueue.push(enableKameleoonLegalConsent);

		// Modus 1: Kameleoon-gesteuerte Experimente
		// Bereits aktive Experimente an Reporting-Tools (GA4, Contentsquare, ...) melden
		window.kameleoonQueue.push(trackActiveExperiments);

		// Beide Modi: Kameleoon::ExperimentActivated → an alle Reporting-Tools (GA4, CS, ...) melden
		observeExperimentActivations();

		// Event-Handler für Conversions registrieren
		registerKameleoonEventHandlers();
	} catch (error) {
		console.error('Fehler bei Kameleoon-Initialisierung:', error);
	}
}

/**
 * Holt den Kameleoon Visitor Code aus dem Cookie
 * @returns {Promise<string|null>} Visitor Code oder null wenn nicht vorhanden
 */
async function getVisitorCode() {
	const cookie = await cookieStore.get(KAMELEOON_CONFIG.VISITOR_COOKIE);
	return cookie?.value || null;
}

/**
 * Generiert eine zufällige Nonce (16 hexadezimale Zeichen)
 * @returns {string} Zufällige Nonce
 */
function generateNonce() {
	const array = new Uint8Array(8);
	crypto.getRandomValues(array);
	return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Holt oder generiert eine Tab-ID für Multi-Tab-Tracking
 * Die Tab-ID wird in sessionStorage gespeichert und ist eindeutig pro Browser-Tab
 * Format: 8 hexadezimale Zeichen (z.B. "F7ECD9AB") wie von Kameleoon engine.js verwendet
 * @returns {string|null} Tab-ID im Hex-Format oder null bei Fehler
 */
function getTabId() {
	try {
		let tabId = sessionStorage.getItem('kameleoonTabId');
		if (!tabId) {
			// 8 hexadezimale Zeichen generieren (wie Kameleoon engine.js)
			// Generiere 4 zufällige Bytes = 8 Hex-Zeichen
			const array = new Uint8Array(4);
			crypto.getRandomValues(array);
			tabId = Array.from(array, byte => byte.toString(16).padStart(2, '0').toUpperCase()).join('');
			sessionStorage.setItem('kameleoonTabId', tabId);
		}
		return tabId;
	} catch (error) {
		// sessionStorage könnte blockiert sein
		return null;
	}
}

/**
 * Sendet ein Goal-Conversion-Event an die Kameleoon REST API
 * @param {number} goalId - Kameleoon Goal ID
 * @param {number} [revenue] - Optional: Revenue-Wert
 * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
 */
async function sendConversionToRestAPI(goalId, revenue = null) {
	const visitorCode = await getVisitorCode();

	if (!visitorCode) {
		console.warn('Kameleoon: Visitor Code nicht verfügbar, Conversion wird nicht gesendet');
		return false;
	}

	const eventData = {
		nonce: generateNonce(),
		eventType: 'CONVERSION',
		goalId: goalId
	};

	// Revenue nur hinzufügen wenn vorhanden
	if (revenue !== null && revenue !== undefined) {
		eventData.revenue = revenue;
	}

	// Query-Parameter wie offizielle Kameleoon Client API:
	// - itp=false (Intelligent Tracking Prevention)
	// - tabId (optional, für Multi-Tab-Tracking)
	// - KEIN json=true (wird von Official API nicht verwendet)
	const tabId = getTabId();
	const url = `${KAMELEOON_CONFIG.API_ENDPOINT}/visit/events?siteCode=${SITE_CODE}&visitorCode=${visitorCode}&itp=false${tabId ? `&tabId=${tabId}` : ''}`;

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify([eventData])
		});

		if (response.ok) {
			return true;
		} else {
			console.error(`Kameleoon: Fehler beim Senden von Goal ${goalId}`, response.status, response.statusText);
			return false;
		}
	} catch (error) {
		console.error(`Kameleoon: Netzwerkfehler beim Senden von Goal ${goalId}`, error);
		return false;
	}
}

/**
 * Sendet ein Goal-Conversion-Event an Kameleoon via Client API (Command Queue)
 * @param {number} goalId - Kameleoon Goal ID
 * @param {number} [revenue] - Optional: Revenue-Wert
 * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
 */
async function sendConversionToClientAPI(goalId, revenue = null) {
	try {
		// Queue ist immer sicher, auch wenn Kameleoon noch nicht geladen ist
		window.kameleoonQueue = window.kameleoonQueue || [];

		if (revenue !== null && revenue !== undefined) {
			window.kameleoonQueue.push(['Kameleoon.API.Goals.processConversion', goalId, revenue]);
		} else {
			window.kameleoonQueue.push(['Kameleoon.API.Goals.processConversion', goalId]);
		}

		return true;
	} catch (error) {
		console.error(`Kameleoon: Fehler beim Senden von Goal ${goalId} via Client API`, error);
		return false;
	}
}

/**
 * Sendet ein Goal-Conversion-Event an Kameleoon (REST oder Client API je nach Konfiguration)
 * @param {number} goalId - Kameleoon Goal ID
 * @param {number} [revenue] - Optional: Revenue-Wert
 * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
 */
async function sendConversionToAPI(goalId, revenue = null) {
	if (KAMELEOON_CONFIG.API_MODE === 'client') {
		return sendConversionToClientAPI(goalId, revenue);
	} else {
		return sendConversionToRestAPI(goalId, revenue);
	}
}

/**
 * Sendet mehrere Events (Goals + Custom Data) in einem einzigen Request an die Kameleoon REST API
 * Verwendet das JSON-Format der offiziellen Kameleoon REST API
 * @param {Array} events - Array von Event-Objekten
 * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
 */
async function sendEventsToRestAPI(events) {
	const visitorCode = await getVisitorCode();

	if (!visitorCode) {
		console.warn('Kameleoon: Visitor Code nicht verfügbar, Events werden nicht gesendet');
		return false;
	}

	// Query-Parameter für REST API:
	// - siteCode, visitorCode
	// - json=true (aktiviert JSON-Modus)
	const url = `${KAMELEOON_CONFIG.API_ENDPOINT}/visit/events?siteCode=${SITE_CODE}&visitorCode=${visitorCode}&json=true`;

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(events)
		});

		if (response.ok) {
			return true;
		} else {
			console.error('Kameleoon: Fehler beim Senden von Events', response.status, response.statusText);
			return false;
		}
	} catch (error) {
		console.error('Kameleoon: Netzwerkfehler beim Senden von Events', error);
		return false;
	}
}

/**
 * Sendet mehrere Events (Goals + Custom Data) an Kameleoon via Client API (Command Queue)
 * Custom Data wird VOR den Goals zur Queue hinzugefügt, um korrekte Reihenfolge zu garantieren
 * @param {Array} events - Array von Event-Objekten
 * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
 */
async function sendEventsToClientAPI(events) {
	try {
		window.kameleoonQueue = window.kameleoonQueue || [];

		// Events der Reihe nach zur Queue hinzufügen
		events.forEach(event => {
			if (event.eventType === 'CUSTOMDATA') {
				// Custom Data: index und erster Wert aus valuesCountMap
				const value = Object.keys(event.valuesCountMap)[0];
				window.kameleoonQueue.push(['Data.setCustomData', event.index, value]);
			} else if (event.eventType === 'CONVERSION') {
				// Goal Conversion mit optionalem Revenue
				if (event.revenue !== undefined) {
					window.kameleoonQueue.push(['Kameleoon.API.Goals.processConversion', event.goalId, event.revenue]);
				} else {
					window.kameleoonQueue.push(['Kameleoon.API.Goals.processConversion', event.goalId]);
				}
			}
		});

		return true;
	} catch (error) {
		console.error('Kameleoon: Fehler beim Senden von Events via Client API', error);
		return false;
	}
}

/**
 * Sendet mehrere Events (Goals + Custom Data) an Kameleoon (REST oder Client API je nach Konfiguration)
 * @param {Array} events - Array von Event-Objekten
 * @returns {Promise<boolean>} true bei Erfolg, false bei Fehler
 */
async function sendEventsToAPI(events) {
	if (KAMELEOON_CONFIG.API_MODE === 'client') {
		return sendEventsToClientAPI(events);
	} else {
		return sendEventsToRestAPI(events);
	}
}

/**
 * Aktiviert Legal Consent für Kameleoon
 * Wird via Queue ausgeführt, sobald Kameleoon.API verfügbar ist
 */
function enableKameleoonLegalConsent() {
	try {
		Kameleoon.API.Core.enableLegalConsent('BOTH');
	} catch (error) {
		console.warn('Kameleoon: Legal Consent konnte nicht aktiviert werden', error);
	}
}

/**
 * Trackt bereits aktive A/B-Test-Experimente beim Service-Start
 * Erfasst alle Experimente, die auf der aktuellen Seite aktiv sind
 */
function trackActiveExperiments() {
	try {
		const experiments = Kameleoon.API.Experiments.getActive();

		if (!experiments || experiments.length === 0) {
			return;
		}

		experiments.forEach(experiment => {
			sendExperienceImpression(experiment);
		});
	} catch (error) {
		console.error('Kameleoon: Fehler beim Tracking aktiver Experimente', error);
	}
}

/**
 * Beide Modi: Hört auf Kameleoon::ExperimentActivated und meldet an alle Reporting-Tools.
 * - Modus 1: Neu aktivierte Kameleoon-Experimente
 * - Modus 2: Hybrid-Experimente nach Explicit Trigger (ausgelöst durch experience_impression-Handler)
 */
function observeExperimentActivations() {
	document.addEventListener('Kameleoon::ExperimentActivated', (event) => {
		try {
			sendExperienceImpression(event.detail);
		} catch (error) {
			console.error('Kameleoon: Fehler beim Event-Listener für Experimente', error);
		}
	});
}

/**
 * Sendet ein experience_impression Event an den DataLayer via dl-event Element
 * @param {Object} experiment - Experiment-Daten von Kameleoon
 * @param {number} experiment.id - Experiment ID
 * @param {Object} experiment.associatedVariation - Zugewiesene Variante
 * @param {number} experiment.associatedVariation.id - Varianten ID
 */
function sendExperienceImpression(experiment) {
	if (!experiment || !experiment.id || !experiment.associatedVariation) {
		console.warn('Kameleoon: Ungültige Experiment-Daten', experiment);
		return;
	}

	const variationId = experiment.associatedVariation.id;

	if (document.querySelector(`dl-event[name="experience_impression"][experiment-id="${experiment.id}"]`)) {
		return;
	}

	document.body.insertAdjacentHTML('beforeend',
		`<dl-event name="experience_impression" experiment-id="${experiment.id}" variant-id="${variationId}"></dl-event>`
	);
}

/**
 * Registriert Event-Handler für DataLayer-Events
 */
function registerKameleoonEventHandlers() {
	// Modus 2: Serverseitige Hybrid-Experimente
	// Server setzt <dl-event name="experience_impression" experiment-id="..." variant-id="...">
	// → wir melden die Variante via Activation API an Kameleoon
	registerEventHandler('experience_impression', (_model, message) => {
		const experimentId = parseInt(message?.experiment_id, 10);
		const variationId  = parseInt(message?.variant_id,  10);
		if (isNaN(experimentId) || isNaN(variationId)) return;

		window.kameleoonQueue = window.kameleoonQueue || [];
		window.kameleoonQueue.push(function() {
			const activeIds = new Set(Kameleoon.API.Experiments.getActive().map(e => e.id));
			if (activeIds.has(experimentId)) return;
			Kameleoon.API.Experiments.assignVariation(experimentId, variationId);
			Kameleoon.API.Experiments.trigger(experimentId, true);
		});
	}, 'kameleoon');

	// Page View Event - für seitentypbasierte Goals und dynamische Goals via kam-goal Attribut
	registerEventHandler('page_view', (model) => {
		const seitentyp = model.seitentyp;
		const kamGoal = model['kam-goal'];

		// Dynamisches Goal via kam-goal Attribut (höchste Priorität)
		if (kamGoal) {
			const goalId = parseInt(kamGoal, 10);
			if (!isNaN(goalId)) {
				sendConversionToAPI(goalId);
			}
		}

		// Seitentypbasierte Goals (Legacy-Support)
		if (seitentyp === 'produkt') {
			sendConversionToAPI(GOALS.PDP);
		}

		if (seitentyp === 'warenkorb') {
			sendConversionToAPI(GOALS.CART_PAGE);
		}

		if (seitentyp === 'bestelluebersicht') {
			sendConversionToAPI(GOALS.ORDER_OVERVIEW);
		}
	}, 'kameleoon');

	// Click Event - für dynamische Goals via kam-goal Attribut
	registerEventHandler('click', (model) => {
		const kamGoal = model.kam_goal;

		if (kamGoal) {
			const goalId = parseInt(kamGoal, 10);
			if (!isNaN(goalId)) {
				sendConversionToAPI(goalId);
			}
		}
	}, 'kameleoon');

	// Search Event
	registerEventHandler('search', () => {
		sendConversionToAPI(GOALS.SEARCH);
	}, 'kameleoon');

	// Add to Cart Event
	registerEventHandler('add_to_cart', (model, message) => {
		sendConversionToAPI(GOALS.ADD_TO_CART);
	}, 'kameleoon');

	// Purchase Event
	registerEventHandler('purchase', (model, message) => {
		// Purchase-Event Struktur (GA4): { event: "purchase", ecommerce: { transaction_id, value, currency, items: [...], ... } }
		const revenue = message?.ecommerce?.value;
		const products = message?.ecommerce?.items;
		const transactionId = message?.ecommerce?.transaction_id;
		const filialabholung = message?.ecommerce?.filialabholung;
		const zahlart = message?.ecommerce?.zahlart;

		if (revenue && products) {
			// Alle Purchase-Events in einem Call senden
			const events = [];

			// WICHTIG: Custom Data MUSS VOR den Goals gesendet werden!
			// Kameleoon verwendet den "most recently tracked value" zum Zeitpunkt der Conversion.
			// Siehe: https://developers.kameleoon.com/core-concepts/custom-data/

			// Custom Data: Bestelltyp (nur wenn vorhanden)
			if (filialabholung) {
				let bestelltyp = "Versand";
				if(filialabholung === "true") {
					bestelltyp = "Click & Collect";
				}
				events.push({
					nonce: generateNonce(),
					eventType: 'CUSTOMDATA',
					index: CUSTOM_DATA.BESTELLTYP,
					valuesCountMap: { [bestelltyp]: 1 },
					mappingIdentifier: false,
					overwrite: true
				});
			}

			// Custom Data: Bestellnummer (nur wenn vorhanden)
			if (transactionId) {
				events.push({
					nonce: generateNonce(),
					eventType: 'CUSTOMDATA',
					index: CUSTOM_DATA.BESTELLNUMMER,
					valuesCountMap: { [String(transactionId)]: 1 },
					mappingIdentifier: false,
					overwrite: true
				});
			}

			// Custom Data: Zahlart (nur wenn vorhanden)
			if (zahlart) {
				events.push({
					nonce: generateNonce(),
					eventType: 'CUSTOMDATA',
					index: CUSTOM_DATA.ZAHLART,
					valuesCountMap: { [String(zahlart)]: 1 },
					mappingIdentifier: false,
					overwrite: true
				});
			}

			// Conversion Goal: Order mit Revenue
			events.push({
				nonce: generateNonce(),
				eventType: 'CONVERSION',
				goalId: GOALS.ORDER_WITH_REVENUE,
				revenue: parseFloat(revenue)
			});

			// Conversion Goal: Durchschnittliche Bestellmenge
			const totalQuantity = products.reduce((acc, item) => {
				const qty = parseInt(item.quantity, 10);
				return acc + (isNaN(qty) ? 0 : qty);
			}, 0);
			events.push({
				nonce: generateNonce(),
				eventType: 'CONVERSION',
				goalId: GOALS.AVG_ORDER_QUANTITY,
				revenue: totalQuantity
			});

			// Alle Events in einem Request senden
			sendEventsToAPI(events);
		}
	}, 'kameleoon');

	// Sign Up Event - Registrierung abgeschlossen
	registerEventHandler('sign_up', () => {
		sendConversionToAPI(GOALS.REGISTRATION);
	}, 'kameleoon');
}

// Self-Registration beim Modulstart
serviceRegistry.registerService({
	name: "Kameleoon",
	initFunction: initKameleoon,
	requiresConsent: true, // Service benötigt Usercentrics-Consent
	configs: [kameleoonInteg, kameleoonProd]
});
